/**
 * Base Offline Service - Abstract Class
 *
 * Provides DRY implementation for all offline entity services.
 * Handles:
 * - Reading from IndexedDB (cache-first strategy)
 * - Writing to IndexedDB with sync queue
 * - Optimistic updates
 * - Conflict resolution (last-write-wins)
 * - Server sync operations
 *
 * Entity-specific services extend this class.
 */

import type { Table } from 'dexie';
import type { PaginatedResponse } from '#/api';

import { offlineDb } from '../database/offlineDatabase';
import useSyncStore from '../stores/syncStore';
import type {
  EntityType,
  OperationType,
  OfflineEntity,
  OfflineMetadata,
  SyncQueueItem,
  SyncResult,
  ApiServiceMethods,
  generateTempId,
  isTemporaryId,
  DEFAULT_MAX_RETRIES,
} from '../types/offline.types';
import {
  generateTempId as genTempId,
  isTemporaryId as isTempId,
  DEFAULT_MAX_RETRIES as MAX_RETRIES,
} from '../types/offline.types';

// ============================================
// ABSTRACT BASE CLASS
// ============================================

/**
 * Abstract base class for offline-enabled entity services
 *
 * Type Parameters:
 * - TEntity: The entity type (e.g., Transaction, Contact)
 * - TCreate: The create input type
 * - TUpdate: The update input type
 */
export abstract class BaseOfflineService<
  TEntity extends { id: string },
  TCreate,
  TUpdate
> {
  /** Entity type identifier */
  protected abstract readonly entityType: EntityType;

  /** Dexie table for this entity */
  protected abstract readonly table: Table<OfflineEntity<TEntity>, string>;

  /** API service methods for online operations */
  protected abstract readonly apiService: ApiServiceMethods<TEntity, TCreate, TUpdate>;

  // ============================================
  // READ OPERATIONS (Cache-First)
  // ============================================

  /**
   * Get all entities from cache
   * Falls back to server fetch if cache is empty and online
   */
  async getAll(filters?: Record<string, unknown>): Promise<TEntity[]> {
    try {
      // First, try to get from cache
      let cachedItems = await this.table.toArray();

      // Apply filters if provided
      if (filters && Object.keys(filters).length > 0) {
        cachedItems = this.applyFilters(cachedItems, filters);
      }

      // If we have cached data, return it
      if (cachedItems.length > 0) {
        return cachedItems.map(this.stripOfflineMetadata);
      }

      // If online and cache is empty, fetch from server
      if (navigator.onLine) {
        const serverData = await this.apiService.getAll(filters);
        const items = this.extractItems(serverData);

        // Cache the fetched items
        await this.cacheItems(items);

        return items;
      }

      // Offline with empty cache
      return [];
    } catch (error) {
      console.error(`[${this.entityType}] Error in getAll:`, error);
      throw error;
    }
  }

  /**
   * Get a single entity by ID
   */
  async getById(id: string): Promise<TEntity | undefined> {
    try {
      // Check if it's a temporary ID - only exists locally
      if (isTempId(id)) {
        const cached = await this.table.get(id);
        return cached ? this.stripOfflineMetadata(cached) : undefined;
      }

      // Try cache first
      const cached = await this.table.get(id);
      if (cached) {
        return this.stripOfflineMetadata(cached);
      }

      // If online, fetch from server
      if (navigator.onLine) {
        const entity = await this.apiService.getOne(id);
        await this.cacheItem(entity);
        return entity;
      }

      return undefined;
    } catch (error) {
      console.error(`[${this.entityType}] Error in getById:`, error);
      throw error;
    }
  }

  // ============================================
  // WRITE OPERATIONS (Offline-First)
  // ============================================

  /**
   * Create a new entity
   * Works offline by using temporary ID and queuing for sync
   */
  async create(data: TCreate): Promise<TEntity> {
    const tempId = genTempId();
    const now = Date.now();

    // Create entity with temporary ID
    const entity: TEntity = {
      ...data,
      id: tempId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    } as TEntity;

    // Add offline metadata
    const offlineEntity: OfflineEntity<TEntity> = {
      ...entity,
      _offlineCachedAt: now,
      _offlineSynced: false,
      _offlineVersion: 1,
      _isTemporary: true,
      _clientTimestamp: now,
    };

    // Store in IndexedDB
    await this.table.add(offlineEntity);

    // Queue for sync
    await this.queueOperation('create', tempId, data as Record<string, unknown>);

    // Update sync store
    useSyncStore.getState().actions.incrementPending(this.entityType);

    // If online, try to sync immediately
    if (navigator.onLine) {
      this.syncItem(tempId).catch((error) => {
        console.warn(`[${this.entityType}] Background sync failed for ${tempId}:`, error);
      });
    }

    return entity;
  }

  /**
   * Update an existing entity
   */
  async update(id: string, data: TUpdate): Promise<TEntity> {
    const now = Date.now();

    // Get existing entity
    const existing = await this.table.get(id);
    if (!existing) {
      throw new Error(`Entity ${id} not found`);
    }

    // Merge updates
    const updated: OfflineEntity<TEntity> = {
      ...existing,
      ...data,
      updatedAt: new Date().toISOString(),
      _offlineSynced: false,
      _offlineVersion: existing._offlineVersion + 1,
      _clientTimestamp: now,
    } as OfflineEntity<TEntity>;

    // Update in IndexedDB
    await this.table.put(updated);

    // Queue for sync (use real ID, not temp)
    const syncId = isTempId(id) ? id : id;
    await this.queueOperation('update', syncId, data as Record<string, unknown>);

    // Update sync store
    useSyncStore.getState().actions.incrementPending(this.entityType);

    // If online, try to sync immediately
    if (navigator.onLine && !isTempId(id)) {
      this.syncItem(id).catch((error) => {
        console.warn(`[${this.entityType}] Background sync failed for ${id}:`, error);
      });
    }

    return this.stripOfflineMetadata(updated);
  }

  /**
   * Delete an entity
   */
  async delete(id: string): Promise<void> {
    // If it's a temporary ID, just remove from local storage
    if (isTempId(id)) {
      await this.table.delete(id);

      // Remove any pending queue items for this temp ID
      await offlineDb.syncQueue
        .where('entityId')
        .equals(id)
        .delete();

      return;
    }

    // Mark as deleted in cache (soft delete)
    const existing = await this.table.get(id);
    if (existing) {
      await this.table.update(id, {
        _offlineSynced: false,
        _clientTimestamp: Date.now(),
      } as Partial<OfflineEntity<TEntity>>);
    }

    // Queue for sync
    await this.queueOperation('delete', id, {});

    // Update sync store
    useSyncStore.getState().actions.incrementPending(this.entityType);

    // If online, try to sync immediately
    if (navigator.onLine) {
      this.syncItem(id).catch((error) => {
        console.warn(`[${this.entityType}] Background sync failed for ${id}:`, error);
      });
    }
  }

  // ============================================
  // SYNC OPERATIONS
  // ============================================

  /**
   * Sync all data from server to local cache
   */
  async syncFromServer(): Promise<void> {
    if (!navigator.onLine) {
      console.log(`[${this.entityType}] Cannot sync from server - offline`);
      return;
    }

    try {
      // Update sync metadata
      await offlineDb.updateSyncMetadata(this.entityType, { status: 'syncing' });

      // Fetch all data from server
      const serverData = await this.apiService.getAll();
      const items = this.extractItems(serverData);

      // Get all local items
      const localItems = await this.table.toArray();
      const localItemsMap = new Map(localItems.map((item) => [item.id, item]));

      // Process each server item
      await offlineDb.transaction('rw', this.table, async () => {
        for (const serverItem of items) {
          const localItem = localItemsMap.get(serverItem.id);

          if (localItem) {
            // Conflict resolution: last-write-wins
            const resolved = this.resolveConflict(localItem, serverItem);
            await this.cacheItem(resolved);
          } else {
            // New item from server
            await this.cacheItem(serverItem);
          }

          // Remove from map (remaining items are locally-only)
          localItemsMap.delete(serverItem.id);
        }
      });

      // Update sync metadata
      await offlineDb.updateSyncMetadata(this.entityType, {
        status: 'idle',
        lastSyncedAt: Date.now(),
      });

      console.log(`[${this.entityType}] Synced ${items.length} items from server`);
    } catch (error) {
      console.error(`[${this.entityType}] Error syncing from server:`, error);
      await offlineDb.updateSyncMetadata(this.entityType, {
        status: 'error',
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });
      throw error;
    }
  }

  /**
   * Sync a single item to the server
   */
  async syncItem(entityId: string): Promise<SyncResult<TEntity>> {
    // Get pending queue item
    const queueItem = await offlineDb.syncQueue
      .where('entityId')
      .equals(entityId)
      .and((item) => item.status === 'pending')
      .first();

    if (!queueItem) {
      return { success: true, data: undefined };
    }

    return this.processSyncQueueItem(queueItem);
  }

  /**
   * Process a sync queue item
   */
  async processSyncQueueItem(queueItem: SyncQueueItem): Promise<SyncResult<TEntity>> {
    try {
      // Mark as syncing
      await offlineDb.syncQueue.update(queueItem.id, {
        status: 'syncing',
        lastAttemptAt: Date.now(),
      });

      let result: TEntity | undefined;
      let serverId: string | undefined;

      switch (queueItem.operation) {
        case 'create': {
          result = await this.apiService.create(queueItem.payload as TCreate);
          serverId = result.id;

          // Map temp ID to server ID
          await offlineDb.addIdMapping(queueItem.entityId, serverId, this.entityType);

          // Update local cache with server ID
          await this.updateLocalIdAfterSync(queueItem.entityId, result);
          break;
        }

        case 'update': {
          // Resolve temp ID if needed
          const realId = await this.resolveId(queueItem.entityId);
          result = await this.apiService.update(realId, queueItem.payload as TUpdate);

          // Update local cache
          await this.cacheItem(result);
          break;
        }

        case 'delete': {
          // Resolve temp ID if needed
          const realId = await this.resolveId(queueItem.entityId);
          await this.apiService.delete(realId);

          // Remove from local cache
          await this.table.delete(queueItem.entityId);
          break;
        }
      }

      // Mark as completed (remove from queue)
      await offlineDb.syncQueue.delete(queueItem.id);

      // Update sync store
      useSyncStore.getState().actions.decrementPending(this.entityType);

      return { success: true, data: result, serverId };
    } catch (error) {
      // Update retry count
      const newRetryCount = queueItem.retryCount + 1;
      const isFailed = newRetryCount >= queueItem.maxRetries;

      await offlineDb.syncQueue.update(queueItem.id, {
        status: isFailed ? 'failed' : 'pending',
        retryCount: newRetryCount,
        lastAttemptAt: Date.now(),
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });

      if (isFailed) {
        useSyncStore.getState().actions.decrementPending(this.entityType);
        useSyncStore.getState().actions.incrementFailed(this.entityType);
      }

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  // ============================================
  // QUEUE MANAGEMENT
  // ============================================

  /**
   * Queue an operation for sync
   */
  protected async queueOperation(
    operation: OperationType,
    entityId: string,
    payload: Record<string, unknown>
  ): Promise<void> {
    const queueItem: SyncQueueItem = {
      id: `${this.entityType}_${entityId}_${Date.now()}`,
      entityType: this.entityType,
      operation,
      entityId,
      payload,
      status: 'pending',
      retryCount: 0,
      maxRetries: MAX_RETRIES,
      createdAt: Date.now(),
      lastAttemptAt: null,
      errorMessage: null,
      clientTimestamp: Date.now(),
    };

    await offlineDb.syncQueue.add(queueItem);
  }

  /**
   * Get all pending queue items for this entity type
   */
  async getPendingQueueItems(): Promise<SyncQueueItem[]> {
    return offlineDb.syncQueue
      .where('[entityType+status]')
      .equals([this.entityType, 'pending'])
      .sortBy('createdAt');
  }

  /**
   * Get all failed queue items for this entity type
   */
  async getFailedQueueItems(): Promise<SyncQueueItem[]> {
    return offlineDb.syncQueue
      .where('[entityType+status]')
      .equals([this.entityType, 'failed'])
      .sortBy('createdAt');
  }

  /**
   * Retry failed queue items
   */
  async retryFailedItems(): Promise<void> {
    const failedItems = await this.getFailedQueueItems();

    for (const item of failedItems) {
      await offlineDb.syncQueue.update(item.id, {
        status: 'pending',
        retryCount: 0,
        errorMessage: null,
      });

      useSyncStore.getState().actions.decrementPending(this.entityType);
      useSyncStore.getState().actions.resetFailed(this.entityType);
    }
  }

  // ============================================
  // CACHE MANAGEMENT
  // ============================================

  /**
   * Cache a single item
   */
  async cacheItem(entity: TEntity): Promise<void> {
    const offlineEntity: OfflineEntity<TEntity> = {
      ...entity,
      _offlineCachedAt: Date.now(),
      _offlineSynced: true,
      _offlineVersion: 1,
      _isTemporary: false,
      _clientTimestamp: Date.now(),
    };

    await this.table.put(offlineEntity);
  }

  /**
   * Cache multiple items
   */
  async cacheItems(entities: TEntity[]): Promise<void> {
    const now = Date.now();
    const offlineEntities: OfflineEntity<TEntity>[] = entities.map((entity) => ({
      ...entity,
      _offlineCachedAt: now,
      _offlineSynced: true,
      _offlineVersion: 1,
      _isTemporary: false,
      _clientTimestamp: now,
    }));

    await this.table.bulkPut(offlineEntities);
  }

  /**
   * Clear all cached items for this entity type
   */
  async clearCache(): Promise<void> {
    await this.table.clear();
  }

  // ============================================
  // CONFLICT RESOLUTION
  // ============================================

  /**
   * Resolve conflict between local and server data
   * Default: Last-write-wins based on _clientTimestamp
   */
  protected resolveConflict(
    clientData: OfflineEntity<TEntity>,
    serverData: TEntity
  ): TEntity {
    // If client data was modified after server sync, keep client data
    const serverTimestamp = new Date((serverData as { updatedAt?: string }).updatedAt ?? 0).getTime();

    if (clientData._clientTimestamp > serverTimestamp && !clientData._offlineSynced) {
      // Client wins - return client data without offline metadata
      return this.stripOfflineMetadata(clientData);
    }

    // Server wins
    return serverData;
  }

  // ============================================
  // HELPER METHODS
  // ============================================

  /**
   * Strip offline metadata from an entity
   */
  protected stripOfflineMetadata(entity: OfflineEntity<TEntity>): TEntity {
    const {
      _offlineCachedAt,
      _offlineSynced,
      _offlineVersion,
      _isTemporary,
      _clientTimestamp,
      ...rest
    } = entity;
    return rest as TEntity;
  }

  /**
   * Extract items from API response (handles pagination)
   */
  protected extractItems(response: TEntity[] | PaginatedResponse<TEntity>): TEntity[] {
    if (Array.isArray(response)) {
      return response;
    }
    return response.data;
  }

  /**
   * Apply filters to cached items
   * Override in subclasses for entity-specific filtering
   */
  protected applyFilters(
    items: OfflineEntity<TEntity>[],
    filters: Record<string, unknown>
  ): OfflineEntity<TEntity>[] {
    return items.filter((item) => {
      for (const [key, value] of Object.entries(filters)) {
        if (value === undefined || value === null || value === '') continue;

        const itemValue = (item as Record<string, unknown>)[key];
        if (itemValue !== value) {
          return false;
        }
      }
      return true;
    });
  }

  /**
   * Resolve a potentially temporary ID to server ID
   */
  protected async resolveId(id: string): Promise<string> {
    if (!isTempId(id)) {
      return id;
    }

    const serverId = await offlineDb.getServerId(id);
    if (!serverId) {
      throw new Error(`Cannot resolve temporary ID: ${id}`);
    }

    return serverId;
  }

  /**
   * Update local cache after successful create sync
   * Replaces temp ID entity with server ID entity
   */
  protected async updateLocalIdAfterSync(
    tempId: string,
    serverEntity: TEntity
  ): Promise<void> {
    // Remove entity with temp ID
    await this.table.delete(tempId);

    // Add entity with server ID
    await this.cacheItem(serverEntity);
  }

  /**
   * Check if entity exists in cache
   */
  async exists(id: string): Promise<boolean> {
    const count = await this.table.where('id').equals(id).count();
    return count > 0;
  }

  /**
   * Get sync status for an entity
   */
  async getSyncStatus(id: string): Promise<'synced' | 'pending' | 'failed' | 'not_found'> {
    const entity = await this.table.get(id);
    if (!entity) {
      return 'not_found';
    }

    if (entity._offlineSynced) {
      return 'synced';
    }

    // Check if there's a failed queue item
    const failedItem = await offlineDb.syncQueue
      .where('entityId')
      .equals(id)
      .and((item) => item.status === 'failed')
      .first();

    if (failedItem) {
      return 'failed';
    }

    return 'pending';
  }
}
